import { Box, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FilterElementProps } from '../index';
import { gridAPI } from 'api/grid';
import { FormComponentValue } from 'components/common/form/layout/control';
import ReactSelect from 'components/common/react-select/ReactSelect';
import useBundleTranslation from 'i18n';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import { resolveDependentUrl } from './dependentFilters';

function SelectFilter({
    name,
    id,
    value,
    onChange,
    label,
    dataUrl,
    dependsOn,
    dependsOnValue,
    isMulti,
    filterData,
    width,
    filterWidth,
    placeholder,
}: FilterElementProps) {
    const { t } = useBundleTranslation(['components/common/grid/grid_filter']);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    // TODO: `filterData` is only read as this useState initializer, so a caller that computes it
    // asynchronously (e.g. from a query that hasn't resolved yet on first mount) never sees a
    // later update — the options freeze at whatever `filterData` was on mount. Callers work around
    // this today by remounting (e.g. `key={JSON.stringify(fields)}` in GuardToolbar.tsx). Fix
    // properly with a `useEffect(() => { if (filterData) setData(filterData); }, [filterData])`.
    const [data, setData] = useState<Array<FormComponentValue>>(
        filterData
            ? filterData
            : isMulti
              ? []
              : [
                    {
                        label: t(placeholder ?? 'all_label'),
                        value: '',
                    },
                ],
    );

    const resolvedUrl = resolveDependentUrl(dataUrl, dependsOn, dependsOnValue ?? '');

    const loadOptions = (url: string, onLoaded?: (options: FormComponentValue[]) => void) => {
        setIsLoading(true);
        gridAPI.loadFilter(url).then((response: { data: { status: string; data: FormComponentValue[] } }) => {
            setIsLoading(false);
            if (response.data.status === 'OK' && response.data.data.length > 0) {
                setData(response.data.data);
                onLoaded?.(response.data.data);
            }
        });
    };

    useEffect(() => {
        if (value) {
            onChange(name, value);
        }
    }, []);

    // A dependent filter's options follow its parent; a value the new parent does not offer is dropped.
    useEffect(() => {
        if (!dependsOn || !resolvedUrl) return;
        loadOptions(resolvedUrl, (options) => {
            if (value && !options.some((option) => String(option.value) === String(value))) {
                onChange(name, '');
            }
        });
    }, [dependsOn, resolvedUrl]);

    // A value set from outside (deep link, another grid's cell) needs its label: load the options it lives in.
    useEffect(() => {
        if (dependsOn || !dataUrl || !value || Array.isArray(value)) return;
        if (!data.some((option) => String(option.value) === String(value))) {
            loadOptions(dataUrl);
        }
    }, [value]);

    const handleSelectUpdate = (newValue: any) => {
        onChange(name, newValue);
    };

    const onFilterOpen = () => {
        if (!dependsOn && dataUrl && data.length < 2) {
            loadOptions(dataUrl);
        }
    };
    const containerWidth = filterWidth ? filterWidth + 'px' : undefined;
    const componentWidth = filterWidth ? '100%' : width ? width + 'px' : '144px';

    return (
        <Grid wrap="nowrap" item container xs={'auto'} key={`grid-filter-${id}-${name}`}>
            <Box flexGrow="1" sx={{ display: 'flex', flexWrap: 'nowrap', width: containerWidth }}>
                {label && <StaticAddon>{t(label)}</StaticAddon>}
                <Box sx={{ width: componentWidth }}>
                    <ReactSelect
                        data={data}
                        value={Array.isArray(value) ? value : [value]}
                        update={handleSelectUpdate}
                        isMulti={isMulti}
                        isMultiOneRowMode
                        name={name}
                        data-test={id}
                        onMenuOpen={onFilterOpen}
                        isLoading={isLoading}
                    />
                </Box>
            </Box>
        </Grid>
    );
}

export default SelectFilter;
